/**
 * useOCR.js
 * Loads Tesseract.js (images) + PDF.js (PDFs) on demand.
 * Renders PDF pages to canvas before passing to Tesseract.
 * Reports errors to the backend for admin visibility.
 * Extracts dates, names, and facility names from scanned text.
 */

import { useState, useCallback } from 'react';

// ─── CDN loaders ──────────────────────────────────────────────────────────────

let tesseractPromise = null;
let pdfJsPromise     = null;

function loadTesseract() {
  if (tesseractPromise) return tesseractPromise;
  tesseractPromise = new Promise((resolve, reject) => {
    if (window.Tesseract) { resolve(window.Tesseract); return; }
    const s = document.createElement('script');
    s.src     = 'https://cdnjs.cloudflare.com/ajax/libs/tesseract.js/5.1.0/tesseract.min.js';
    s.onload  = () => window.Tesseract ? resolve(window.Tesseract) : reject(new Error('Tesseract not on window after load'));
    s.onerror = () => reject(new Error('Failed to load Tesseract.js from CDN'));
    document.head.appendChild(s);
  });
  return tesseractPromise;
}

function loadPdfJs() {
  if (pdfJsPromise) return pdfJsPromise;
  pdfJsPromise = new Promise((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const s = document.createElement('script');
    s.src    = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      if (!window.pdfjsLib) { reject(new Error('pdfjsLib not on window')); return; }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    s.onerror = () => reject(new Error('Failed to load PDF.js from CDN'));
    document.head.appendChild(s);
  });
  return pdfJsPromise;
}

// ─── PDF → canvas array ───────────────────────────────────────────────────────

async function pdfToCanvases(file, onPageProgress) {
  const pdfjsLib    = await loadPdfJs();
  const arrayBuffer = await file.arrayBuffer();
  const pdf         = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const maxPages    = Math.min(pdf.numPages, 6);
  const canvases    = [];

  for (let i = 1; i <= maxPages; i++) {
    const page     = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas   = document.createElement('canvas');
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;
    const ctx      = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    canvases.push(canvas);
    if (onPageProgress) onPageProgress(i, maxPages);
  }
  return canvases;
}

// ─── Backend error reporter ───────────────────────────────────────────────────

async function reportOCRError({ caseId, fileName, fileType, errorMessage, errorDetail }) {
  try {
    const token = localStorage.getItem('zeus_token');
    if (!token) return;
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    await fetch(`${apiBase}/ocr/log-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ case_id: caseId || null, file_name: fileName || null, file_type: fileType || null, error_message: errorMessage, error_detail: errorDetail || null }),
    });
  } catch { /* never let error reporting crash anything */ }
}

// ─── Text parsing helpers ─────────────────────────────────────────────────────

function normalize(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

function nameInText(name, text) {
  if (!name || !text) return false;
  const normName = normalize(name);
  const normText = normalize(text);
  if (normText.includes(normName)) return true;
  const parts = normName.split(' ').filter(w => w.length > 3);
  return parts.length > 0 && parts.every(p => normText.includes(p));
}

// ─── Date extraction ──────────────────────────────────────────────────────────

export function extractDates(text) {
  const patterns = [
    /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})\b/g,
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})\b/gi,
    /\b(\d{1,2})(?:st|nd|rd|th)?\s+(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?),?\s+(\d{4})\b/gi,
  ];
  const found = new Set();
  patterns.forEach(re => {
    let m;
    while ((m = re.exec(text)) !== null) found.add(m[0].trim());
  });
  return [...found].slice(0, 12); // cap at 12 dates
}

// ─── Name / entity extraction ─────────────────────────────────────────────────

// Common medical facility keywords — helps flag lines as facility names
const FACILITY_KEYWORDS = [
  'medical center', 'medical clinic', 'hospital', 'clinic', 'health', 'healthcare',
  'rehab', 'rehabilitation', 'orthopedic', 'orthopaedic', 'physical therapy',
  'imaging', 'radiology', 'urgent care', 'er ', 'emergency', 'surgery center',
  'spine', 'neurology', 'chiropractic', 'pain management', 'wellness',
];

// Common titles that precede person names
const NAME_PREFIXES = ['dr\\.?', 'doctor', 'md', 'do', 'pa', 'np', 'rn', 'pt', 'dpt', 'ms\\.?', 'mr\\.?', 'mrs\\.?'];
const PREFIX_RE = new RegExp(`\\b(${NAME_PREFIXES.join('|')})\\b`, 'i');

export function extractEntities(text, { clientName = '', providerNames = [] } = {}) {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 2);

  const detectedNames      = new Set();
  const detectedFacilities = new Set();
  const detectedDates      = extractDates(text);

  // 1. Client name detection
  let clientFound = false;
  if (clientName) {
    const normClient = normalize(clientName);
    for (const line of lines) {
      if (normalize(line).includes(normClient) || nameInText(clientName, line)) {
        clientFound = true;
        break;
      }
    }
  }

  // 2. Known provider names — mark which ones appear
  const foundProviders = providerNames.filter(n => n && nameInText(n, text));

  // 3. Scan lines for facility-like strings
  for (const line of lines) {
    const lower = line.toLowerCase();
    const isFacility = FACILITY_KEYWORDS.some(kw => lower.includes(kw));
    if (isFacility && line.length > 4 && line.length < 80) {
      // Clean up common OCR artifacts
      const clean = line.replace(/[|\\\/]{2,}/g, '').replace(/\s{2,}/g, ' ').trim();
      if (clean.length > 4) detectedFacilities.add(clean);
    }
  }

  // 4. Scan lines for person names (preceded by a title or all caps short line)
  for (const line of lines) {
    // Lines matching "Dr. First Last" or "John Smith, MD"
    if (PREFIX_RE.test(line) && line.length < 60) {
      const clean = line.replace(/[,;|]/g, '').replace(/\s{2,}/g, ' ').trim();
      if (clean.length > 4) detectedNames.add(clean);
    }
    // All-caps short lines that look like names (2–4 words, all caps)
    const words = line.split(/\s+/);
    if (words.length >= 2 && words.length <= 4 &&
        words.every(w => w === w.toUpperCase() && /^[A-Z]{2,}$/.test(w))) {
      // Title-case it
      const titled = words.map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
      if (titled.length > 4 && titled.length < 40) detectedNames.add(titled);
    }
  }

  // 5. Known providers found in the document get highlighted as names too
  foundProviders.forEach(p => detectedNames.add(p));

  return {
    clientFound,
    clientName,
    dates:      detectedDates,
    names:      [...detectedNames].slice(0, 10),
    facilities: [...detectedFacilities].slice(0, 6),
    foundProviders,
  };
}

// ─── Suggestion engine ────────────────────────────────────────────────────────

export function buildSuggestions({ ocrText, consultations = [], treatments = [], clientName = '' }) {
  const suggestions = [];
  const clientInDoc = clientName ? nameInText(clientName, ocrText) : false;

  consultations.forEach((entry, idx) => {
    const provName = entry.provider_name || '';
    if (!provName) return;
    const provInDoc  = nameInText(provName, ocrText);
    const apptType   = entry.appointment_type || 'Appointment';
    const sessionLbl = entry.session_label ? ` (${entry.session_label})` : '';
    const confidence = clientInDoc ? 'high' : 'medium';

    if (provInDoc && entry.status === 'scheduled') {
      suggestions.push({
        id: `consult-show-${idx}`, type: 'consultation_status', confidence,
        title: 'Mark appointment as attended',
        description: `${apptType}${sessionLbl} with ${provName} is "Scheduled". This document appears to be from ${provName}.`,
        action: { consultationIdx: idx, field: 'status', value: 'show' },
      });
    }
    if (provInDoc && entry.status === 'show' && !entry.record_uploaded) {
      suggestions.push({
        id: `consult-record-${idx}`, type: 'record_uploaded', confidence,
        title: 'Mark records received',
        description: `${apptType}${sessionLbl} with ${provName} — this upload can mark the record as received.`,
        action: { consultationIdx: idx, field: 'record_uploaded', value: true },
      });
    }
  });

  const today = new Date().toISOString().split('T')[0];
  treatments.forEach((t, idx) => {
    const provName = t.provider_name || '';
    if (!provName) return;
    const provInDoc  = nameInText(provName, ocrText);
    const confidence = clientInDoc ? 'high' : 'medium';

    if (provInDoc && t.records_requested && !t.records_received_date) {
      suggestions.push({
        id: `treatment-records-${idx}`, type: 'treatment_records', confidence,
        title: `Mark records received from ${provName}`,
        description: `Records were requested from ${provName}. Today's date will be set as the received date.`,
        action: { treatmentIdx: idx, field: 'records_received_date', value: today },
      });
    }
    if (provInDoc && t.bills_requested && !t.bills_received_date) {
      suggestions.push({
        id: `treatment-bills-${idx}`, type: 'treatment_bills', confidence,
        title: `Mark bills received from ${provName}`,
        description: `Bills were requested from ${provName}. Today's date will be set as the received date.`,
        action: { treatmentIdx: idx, field: 'bills_received_date', value: today },
      });
    }
  });

  return { suggestions, dates: extractDates(ocrText), clientInDoc, ocrText };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export default function useOCR() {
  const [status,   setStatus]   = useState('idle');
  const [progress, setProgress] = useState(0);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState(null);

  const runOCR = useCallback(async (file, { consultations, treatments, clientName, caseId }) => {
    setStatus('loading');
    setProgress(0);
    setResult(null);
    setError(null);

    const fileName = file?.name || 'unknown';
    const fileType = file?.type || 'unknown';
    const isPDF    = fileType === 'application/pdf';

    try {
      const Tesseract = await loadTesseract();
      setStatus('scanning');

      let ocrText = '';

      if (isPDF) {
        const canvases = await pdfToCanvases(file, (pageNum, total) => {
          setProgress(Math.round((pageNum / total) * 30));
        });
        const perPage = canvases.length > 0 ? 70 / canvases.length : 70;
        for (let i = 0; i < canvases.length; i++) {
          const { data: { text } } = await Tesseract.recognize(canvases[i], 'eng', {
            logger: (m) => {
              if (m.status === 'recognizing text')
                setProgress(Math.round(30 + (i * perPage) + (m.progress * perPage)));
            },
          });
          ocrText += (text || '') + '\n';
        }
      } else {
        const { data: { text } } = await Tesseract.recognize(file, 'eng', {
          logger: (m) => {
            if (m.status === 'recognizing text')
              setProgress(Math.round(m.progress * 100));
          },
        });
        ocrText = text || '';
      }

      setProgress(100);

      const providerNames = [
        ...new Set([
          ...(consultations || []).map(c => c.provider_name).filter(Boolean),
          ...(treatments    || []).map(t => t.provider_name).filter(Boolean),
        ])
      ];

      const suggestions = buildSuggestions({ ocrText, consultations, treatments, clientName });
      const entities    = extractEntities(ocrText, { clientName, providerNames });

      setResult({ ...suggestions, entities });
      setStatus('done');

    } catch (err) {
      console.error('[OCR]', err);
      const msg = err?.message || 'OCR failed';
      setError(msg);
      setStatus('error');
      await reportOCRError({ caseId, fileName, fileType, errorMessage: msg,
        errorDetail: err?.stack ? err.stack.split('\n').slice(0, 3).join(' | ') : null });
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setProgress(0);
    setResult(null);
    setError(null);
  }, []);

  return { status, progress, result, error, runOCR, reset };
}
